import React, { useCallback, useState, useMemo } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TableCell,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { StyledTableRow } from './StyledTableRow';
import { MarginHighlightCell } from './components/MarginHighlightCell';
import { MemberInfoCell, priceFormat } from '../../components/tableCellRenders';
import { CustomTableCell } from '../../components/customTable';
import { CustomBudgetCell, CustomBudgetBox } from './components';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import ResetIcon from '@mui/icons-material/Restore';
import { MemberRatesUpdateForm } from './types';
import { useFormik } from 'formik';
import { compensationMarginsValidationSchema } from './CompensationMargins.schema';
import VerticalAlignTopRoundedIcon from '@mui/icons-material/VerticalAlignTopRounded';
import VerticalAlignBottomRoundedIcon from '@mui/icons-material/VerticalAlignBottomRounded';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { NumericFormat } from 'react-number-format';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import ReportIssueModal from '../reportIssueModal/ReportIssueModal';
import { ReportIssueVariant } from '../reportIssueModal/ReportIssueModal';
import { CompensationRange, MarginHighlight, Member } from '../../types';
import { RateField } from './components/RateField';
import { iconColor, payRateColor } from '../../utils/business';
import { updateMemberField } from '../../redux/account';
import { useAppDispatch } from '../../redux/hooks';
import {
  MARGINS_NAME_COLUMN_WIDTH,
  marginsActionsColumnBodySx,
  marginsPayRangeBodyCellSx,
} from './marginsTableLayout';

export interface TeamMemberViewProps
  extends Omit<
    Member,
    | 'legalLocation'
    | 'email'
    | 'seniorityLevel'
    | 'expectedPayRate'
    | 'companyRange'
    | 'isManager'
  > {
  grossMarginHighlights: MarginHighlight[];
  netMarginHighlights: MarginHighlight[];
  isLastRow: boolean;
  isRestricted?: boolean | false;
  isHidden?: boolean;
  legalLocation?: string;
  city?: string;
  accountName?: string;

  isEditableMode: boolean;
  projectId?: string;
}

const restrictedUserMessage =
  "You don't have permissions to view financial data for this user";

export const TeamMemberView = ({
  id: memberId,
  fullName,
  seniority,
  stream,
  resource,
  compensationRate,
  companyRateCard,
  allocation,
  grossMargin,
  netMargin,
  grossMarginHighlights,
  netMarginHighlights,
  isLastRow,
  isEditableMode,
  isRestricted,
  isHidden,
  compensationRange,
  avatarUrl,
  actualPayRate,
  compensationType,
  legalLocation,
  city,
  projectId,
  accountName,
}: TeamMemberViewProps) => {
  const [reportMenuVisible, setReportMenuVisible] =
    useState<null | HTMLElement>(null);
  const [reportIncorrectModalVisible, setReportIncorrectModalVisible] =
    useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<null | HTMLElement>(null);
  const open = Boolean(reportMenuVisible);
  const dispatch = useAppDispatch();

  const formik = useFormik<MemberRatesUpdateForm>({
    initialValues: {
      memberId,
      compensationRate: compensationRate ?? undefined,
      billRate: resource?.billRate || 0,
      allocation: allocation ?? 100,
      isHidden: isHidden ?? false,
      payRange: [compensationRange?.min || 0, compensationRange?.max || 0],
    },
    validationSchema: compensationMarginsValidationSchema,
    validateOnMount: true,
    onSubmit: () => {
      return;
    },
    enableReinitialize: true,
  });

  const handleClose = useCallback(() => {
    setReportMenuVisible(null);
  }, [setReportMenuVisible]);

  const handleModalClose = useCallback(() => {
    setReportIncorrectModalVisible(false);
  }, [setReportIncorrectModalVisible]);

  const closeActionsMenu = useCallback(() => {
    setActionsMenuAnchor(null);
  }, []);

  const handleSelectMenu = useCallback(() => {
    setReportIncorrectModalVisible(true);
    setReportMenuVisible(null);
  }, [setReportIncorrectModalVisible]);

  const handleReportMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setReportMenuVisible(event.currentTarget);
    },
    [setReportMenuVisible],
  );

  const onRateReset = useCallback(
    ({
      compensationRate,
      billRate,
      allocation,
    }: {
      compensationRate: number;
      billRate: number;
      allocation: number;
    }) => {
      if (!projectId) return;

      [
        { fieldName: 'compensationRate', value: compensationRate },
        { fieldName: 'billRate', value: billRate },
        { fieldName: 'allocation', value: allocation },
      ].forEach(({ fieldName, value }) => {
        dispatch(
          updateMemberField({
            id: memberId,
            fieldName: fieldName,
            value: value,
            projectId: projectId,
          }),
        );
        formik.setFieldValue(fieldName, value);
      });
    },
    [formik, projectId, memberId, dispatch],
  );

  const onRateChange = useCallback(
    (data: { fieldName: string; value?: number }) => {
      if (!projectId) return;

      formik.setFieldValue(data.fieldName, data.value);
      dispatch(
        updateMemberField({
          id: memberId,
          fieldName: data.fieldName,
          value: data.value,
          projectId,
        }),
      );
    },
    [formik, memberId, projectId, dispatch],
  );

  const onAddRemoveIconClick = useCallback(() => {
    if (!projectId) return;

    formik.setFieldValue('isHidden', !Boolean(formik.values.isHidden));
    dispatch(
      updateMemberField({
        id: memberId,
        fieldName: 'isHidden',
        value: !Boolean(formik.values.isHidden),
        projectId,
      }),
    );
  }, [projectId, memberId, dispatch, formik]);

  const columnWidth = useMemo(() => (theme: Theme) => theme.spacing(10), []);

  return (
    <StyledTableRow isLastRow={isLastRow} isHidden={formik.values.isHidden}>
      <CustomTableCell
        sx={{
          borderBottom: '0',
          width: MARGINS_NAME_COLUMN_WIDTH,
          minWidth: MARGINS_NAME_COLUMN_WIDTH,
          maxWidth: MARGINS_NAME_COLUMN_WIDTH,
          height: 80,
        }}
        pure
        nowrap
      >
        <MemberInfoCell
          userInfo={{
            fullName: fullName,
            stream: `${seniority} ${stream}`.trim(),
            avatarUrl: avatarUrl,
            country: legalLocation,
            city: city,
          }}
        />
      </CustomTableCell>
      {isRestricted ? (
        <>
          <CustomTableCell colSpan={7} pure>
            <Box
              sx={{
                display: 'flex',
                alignContent: 'center',
                pl: 4,
              }}
            >
              <LockOutlinedIcon color="disabled" />
              <Typography
                sx={{ alignSelf: 'center', px: 0.5 }}
                variant="body2"
                color="text.disabled"
              >
                {restrictedUserMessage}
              </Typography>
            </Box>
          </CustomTableCell>
        </>
      ) : (
        <>
          <MarginHighlightCell
            align="center"
            marginHighlights={grossMarginHighlights || []}
            value={grossMargin}
            sx={{ borderBottom: '0', width: columnWidth }}
            marginCategory="Gross Margin"
          >
            {grossMargin == null ? (
              <Tooltip
                title={restrictedUserMessage}
                placement="top"
                sx={{
                  [`& .${tooltipClasses.tooltip}`]: {
                    maxWidth: 'none',
                  },
                }}
              >
                <Box>{'NA'}</Box>
              </Tooltip>
            ) : (
              `${grossMargin}%`
            )}
          </MarginHighlightCell>

          <MarginHighlightCell
            align="center"
            marginHighlights={netMarginHighlights || []}
            value={netMargin}
            sx={{ borderBottom: '0', width: columnWidth }}
            marginCategory="Net Margin"
          >
            {netMargin == null ? (
              <Tooltip
                title={restrictedUserMessage}
                placement="top"
                sx={{
                  [`& .${tooltipClasses.tooltip}`]: {
                    maxWidth: 'none',
                  },
                }}
              >
                <Box>{'NA'}</Box>
              </Tooltip>
            ) : (
              `${netMargin}%`
            )}
          </MarginHighlightCell>

          {isEditableMode ? (
            <CustomBudgetCell justifyContent="center" sx={{ px: 0 }}>
              <RateField
                id={'compensationRate'}
                name={'compensationRate'}
                value={formik.values.compensationRate}
                handleChange={onRateChange}
                error={Boolean(formik.errors.compensationRate)}
                disabled={isHidden}
              />
            </CustomBudgetCell>
          ) : (
            <CustomBudgetCell
              icon={
                <PayRateIcon
                  compensationRange={compensationRange}
                  compensationRate={compensationRate}
                />
              }
              justifyContent="center"
              align="center"
            >
              <Tooltip
                title={
                  actualPayRate
                    ? `$${actualPayRate} ${compensationType}`
                    : !!!compensationRate && restrictedUserMessage
                }
                placement="top"
              >
                <Box>
                  <>
                    {'\u2009'}
                    {compensationRate
                      ? priceFormat({
                          value: compensationRate,
                          hideCents: true,
                        })
                      : 'NA'}
                  </>
                </Box>
              </Tooltip>
            </CustomBudgetCell>
          )}

          <TableCell sx={(theme: Theme) => marginsPayRangeBodyCellSx(theme)}>
            <Tooltip
              title={
                <>
                  Min: ${compensationRange?.min} | Max: $
                  {compensationRange?.max} | Approve with PDM: $
                  {compensationRange?.approvedMax}
                </>
              }
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 'none',
                  },
                },
              }}
            >
              <Typography variant="body1" component="div" align="center">
                {compensationRange?.min == null ? (
                  'NA'
                ) : (
                  <Box
                    sx={{
                      whiteSpace: 'nowrap',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0,
                    }}
                  >
                    {'\u2009'}
                    {priceFormat({
                      value: compensationRange?.min,
                      hideCents: true,
                    })}
                    {'\u2009'}
                    <CustomBudgetBox>..</CustomBudgetBox>
                    {'\u2009'}
                    {'\u2009'}
                    {priceFormat({
                      value: compensationRange?.approvedMax,
                      hideCents: true,
                    })}
                  </Box>
                )}
              </Typography>
            </Tooltip>
          </TableCell>

          {isEditableMode ? (
            <CustomBudgetCell justifyContent="center" sx={{ px: 0 }}>
              <RateField
                id={'billRate'}
                name={'billRate'}
                value={formik.values.billRate}
                handleChange={onRateChange}
                error={Boolean(formik.errors.billRate)}
                disabled={isHidden}
              />
            </CustomBudgetCell>
          ) : (
            <CustomBudgetCell justifyContent="center">
              {'\u2009'}
              {priceFormat({
                value: resource?.billRate,
                hideCents: true,
              })}
            </CustomBudgetCell>
          )}

          <CustomBudgetCell
            align="center"
            sx={{ borderBottom: '0', width: columnWidth }}
            justifyContent="center"
          >
            {companyRateCard?.billRate == null ? (
              'NA'
            ) : (
              <>
                {'\u2009'}
                {priceFormat({
                  value: companyRateCard?.billRate,
                  hideCents: true,
                })}
              </>
            )}
          </CustomBudgetCell>

          {isEditableMode ? (
            <CustomBudgetCell justifyContent="center" sx={{ px: 0 }}>
              <Tooltip
                open={Boolean(formik.errors.allocation)}
                title={formik.errors.allocation ?? ''}
              >
                <Box>
                  <NumericFormat
                    customInput={TextField}
                    autoComplete="off"
                    fullWidth
                    id="allocation"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                      size: 'small',
                      sx: {
                        maxWidth: (theme: Theme) => theme.spacing(8.5),
                        minWidth: (theme: Theme) => theme.spacing(7.5),
                        maxHeight: (theme: Theme) => theme.spacing(4.5),
                      },
                    }}
                    inputProps={{ maxLength: 3 }}
                    name="allocation"
                    inputMode="decimal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={Boolean(formik.errors.allocation)}
                    onValueChange={({ floatValue }) =>
                      onRateChange({
                        fieldName: 'allocation',
                        value: floatValue,
                      })
                    }
                    value={formik.values.allocation}
                    variant="filled"
                    hiddenLabel
                  />
                </Box>
              </Tooltip>
            </CustomBudgetCell>
          ) : (
            <TableCell
              color="text.disabled"
              align="center"
              sx={{ width: columnWidth }}
            >
              {allocation === undefined || allocation === null
                ? 'NA'
                : `${allocation}%`}
            </TableCell>
          )}
        </>
      )}

      {/* Edit icons */}
      {isEditableMode ? (
        <TableCell
          align="center"
          sx={(theme: Theme) => marginsActionsColumnBodySx(theme)}
        >
          <IconButton
            aria-label="Open actions"
            aria-controls={
              actionsMenuAnchor ? 'team-member-actions-edit' : undefined
            }
            aria-expanded={Boolean(actionsMenuAnchor)}
            aria-haspopup="menu"
            onClick={(event) => setActionsMenuAnchor(event.currentTarget)}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="team-member-actions-edit"
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={closeActionsMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 0.5,
                minWidth: 200,
                borderRadius: 1,
                boxShadow: 3,
              },
            }}
          >
            <Typography
              component="div"
              variant="caption"
              sx={{
                px: 2,
                pt: 1,
                pb: 0.5,
                display: 'block',
                color: 'text.secondary',
              }}
            >
              Actions
            </Typography>
            {!isRestricted && (
              <MenuItem
                onClick={() => {
                  onRateReset({
                    compensationRate: compensationRate ?? 0,
                    billRate: resource?.billRate ?? 0,
                    allocation: allocation ?? 100,
                  });
                  closeActionsMenu();
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ResetIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Reset rates" />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                onAddRemoveIconClick();
                closeActionsMenu();
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {formik.values.isHidden ? (
                  <PersonAddOutlinedIcon fontSize="small" />
                ) : (
                  <PersonOffOutlinedIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={formik.values.isHidden ? 'Activate' : 'Deactivate'}
              />
            </MenuItem>
          </Menu>
        </TableCell>
      ) : (
        <TableCell
          align="center"
          sx={(theme: Theme) => marginsActionsColumnBodySx(theme)}
        >
          <IconButton size="small" onClick={handleReportMenu}>
            <MoreVertIcon />
          </IconButton>

          <Menu anchorEl={reportMenuVisible} open={open} onClose={handleClose}>
            <MenuItem onClick={handleSelectMenu}>
              Report incorrect project
            </MenuItem>
          </Menu>
        </TableCell>
      )}

      {reportIncorrectModalVisible ? (
        <>
          <ReportIssueModal
            isModalOpen={reportIncorrectModalVisible}
            handleCloseModal={handleModalClose}
            reportIssueVariant={ReportIssueVariant.ReportIssueIncorrectFields}
            currentUserDetails={{
              fullName,
              stream,
              seniority,
              avatarUrl,
              city,
              country: legalLocation,
              accountName: accountName,
            }}
          />
        </>
      ) : null}
    </StyledTableRow>
  );
};

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 200,
  },
});

const PayRateIcon = ({
  compensationRate,
  compensationRange,
}: {
  compensationRate?: number | null;
  compensationRange?: CompensationRange;
}) => {
  const payRateIconColor = payRateColor({
    compensationRange,
    compensationRate,
  });

  if (compensationRate) {
    if (payRateIconColor === iconColor.aboveRangeIconColor) {
      return (
        <CustomWidthTooltip title="Above pay range" placement="top">
          <VerticalAlignTopRoundedIcon
            sx={{ height: '20px', color: 'error.main' }}
          />
        </CustomWidthTooltip>
      );
    }
    if (payRateIconColor === iconColor.belowRangeIconColor) {
      return (
        <CustomWidthTooltip title="Below pay range" placement="top">
          <VerticalAlignBottomRoundedIcon
            sx={{ height: '20px', color: 'primary.main' }}
          />
        </CustomWidthTooltip>
      );
    }
  }

  return <></>;
};
